/**
 * vl-why-velotric.js — 首页「Why Choose Velotric?」图片墙（子需求 10）
 *
 * 需求：需求/20260922-新版首页改版/子需求/10-首页WhyChooseVelotric/
 *
 * 行为（用户 2026-09-30 口径）：
 *   1) 两行图片墙**一直无限循环自动播放**：上排向左、下排向右（同速，data-vl-speed px/s）
 *   2) **悬停某行 → 该行暂停**（另一行继续）；键盘焦点进入某行也暂停该行
 *   3) **支持左右拖拽**：拖拽中该行暂停、内容跟手；松手后从当前位置继续匀速滚动
 *      （位移 > 6px 视为拖拽，不再触发卡片链接的点击）
 *   4) 进入视口才走、页面隐藏时停表；`prefers-reduced-motion: reduce` 时**不自动滚动**（仍可拖拽）
 *
 * 实现：每条轨道渲染 2 份内容，JS 匀速平移 `translate3d(-(pos mod period))` 并做周期归一 ⇒ 无缝循环。
 */
(function () {
  'use strict';

  var DEFAULT_SPEED = 30; // px/s
  var MAX_TICK_MS = 250; // rAF 单帧最大计入时长（切后台回来不跳变）
  var SETTLE_MS = 150; // resize 后的重新度量防抖
  var DRAG_THRESHOLD = 6; // px：区分拖拽与点击
  var EVENTS = [
    'pointerenter',
    'pointerleave',
    'pointerdown',
    'pointermove',
    'pointerup',
    'pointercancel',
    'click',
    'focusin',
    'focusout',
  ];

  class VLWhyVelotric extends HTMLElement {
    constructor() {
      super();
      this._onTick = this._onTick.bind(this);
      this._onVisibility = this._onVisibility.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onEnter = this._onEnter.bind(this);
      this._onLeave = this._onLeave.bind(this);
      this._onDown = this._onDown.bind(this);
      this._onMove = this._onMove.bind(this);
      this._onUp = this._onUp.bind(this);
      this._onClick = this._onClick.bind(this);
      this._onFocusIn = this._onFocusIn.bind(this);
      this._onFocusOut = this._onFocusOut.bind(this);
    }

    connectedCallback() {
      if (this._ready) {
        // 被重新接回 DOM：只重新绑定监听/观察者并重新度量，不重置位置
        if (this._detached) {
          this._detached = false;
          this._observe();
          this._bind();
          document.addEventListener('visibilitychange', this._onVisibility);
          window.addEventListener('resize', this._onResize);
          window.addEventListener('load', this._onResize);
          this._measure();
          this._evaluate();
        }
        return;
      }
      this._ready = true;

      var self = this;
      this.rows = Array.prototype.slice
        .call(this.querySelectorAll('[data-vl-row]'))
        .map(function (el) {
          return {
            el: el,
            track: el.querySelector('[data-vl-track]'),
            // +1 → pos 递增 → translateY 递减 → 内容向左；-1 → 向右
            dir: el.getAttribute('data-vl-direction') === 'right' ? -1 : 1,
            pos: 0,
            period: 0,
            hovered: false,
            focused: false,
            dragging: false,
          };
        })
        .filter(function (row) {
          return !!row.track;
        });

      this.speed = this._readSpeed();
      this.reduced = !!(
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      );
      this.inView = false;
      this.playing = false;
      this.raf = null;
      this.last = 0;
      this.drag = null;
      this._dragMoved = 0;

      this._measure();
      this._observe();
      this._bind();
      document.addEventListener('visibilitychange', this._onVisibility);
      window.addEventListener('resize', this._onResize);
      window.addEventListener('load', this._onResize);
      self = null;
    }

    disconnectedCallback() {
      this._detached = true;
      this._stop();
      window.clearTimeout(this._resizeTimer);
      if (this.io) {
        this.io.disconnect();
        this.io = null;
      }
      this._unbind();
      document.removeEventListener('visibilitychange', this._onVisibility);
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('load', this._onResize);
    }

    /* ---------------------------------------------------------------- 基本参数 */
    _readSpeed() {
      var value = parseFloat(this.dataset.vlSpeed);
      if (!value || value <= 0 || !isFinite(value)) value = DEFAULT_SPEED;
      return value;
    }

    _rowOf(el) {
      for (var i = 0; i < this.rows.length; i++) {
        if (this.rows[i].el === el) return this.rows[i];
      }
      return null;
    }

    /* ---------------------------------------------------------------- 度量与平移 */
    _measure() {
      for (var i = 0; i < this.rows.length; i++) {
        var row = this.rows[i];
        var items = row.track.children;
        // 第 2 份（副本）由 Liquid 标记 aria-hidden ⇒ 其数量＝一份的卡片数
        var half = row.track.querySelectorAll('[aria-hidden="true"]').length;
        if (!items.length || !half || items.length < half * 2) {
          row.period = 0;
          continue;
        }
        // 用 getBoundingClientRect 取「一份」的精确宽度（含尾随 gap），避免 scrollWidth 取整误差
        var first = items[0].getBoundingClientRect().left;
        var boundary = items[half].getBoundingClientRect().left;
        row.period = boundary - first > 0 ? boundary - first : 0;
      }
      this._apply();
    }

    _wrap(value, period) {
      return ((value % period) + period) % period;
    }

    _applyRow(row) {
      if (!row.period) return;
      var shift = -this._wrap(row.pos, row.period);
      row.track.style.transform = 'translate3d(' + shift.toFixed(2) + 'px,0,0)';
    }

    _apply() {
      for (var i = 0; i < this.rows.length; i++) this._applyRow(this.rows[i]);
    }

    /* ---------------------------------------------------------------- 播放 */
    _observe() {
      var self = this;
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          function (entries) {
            for (var i = 0; i < entries.length; i++) {
              if (entries[i].target === self) self.inView = entries[i].isIntersecting;
            }
            self._evaluate();
          },
          { threshold: 0.1 }
        );
        this.io.observe(this);
      } else {
        this.inView = true;
        this._evaluate();
      }
    }

    _onVisibility() {
      this._evaluate();
    }

    _evaluate() {
      var should =
        this.rows.length > 0 &&
        this.inView &&
        !this.reduced &&
        document.visibilityState !== 'hidden';
      if (should) this._play();
      else this._stop();
    }

    _play() {
      if (this.playing) return;
      this.playing = true;
      this.last = 0;
      this.raf = window.requestAnimationFrame(this._onTick);
    }

    _stop() {
      this.playing = false;
      if (this.raf) {
        window.cancelAnimationFrame(this.raf);
        this.raf = null;
      }
    }

    _onTick(ts) {
      if (!this.playing) return;

      if (!this.last) this.last = ts;
      var dt = ts - this.last;
      this.last = ts;
      if (!(dt > 0)) dt = 0;
      if (dt > MAX_TICK_MS) dt = MAX_TICK_MS;

      for (var i = 0; i < this.rows.length; i++) {
        var row = this.rows[i];
        if (row.dragging || row.hovered || row.focused || !row.period) continue;
        row.pos += (row.dir * this.speed * dt) / 1000;
        this._applyRow(row);
      }

      if (this.playing) this.raf = window.requestAnimationFrame(this._onTick);
    }

    /* ---------------------------------------------------------------- 交互 */
    _bind() {
      for (var i = 0; i < this.rows.length; i++) {
        this._bindRow(this.rows[i].el, true);
      }
    }

    _unbind() {
      if (!this.rows) return;
      for (var i = 0; i < this.rows.length; i++) {
        this._bindRow(this.rows[i].el, false);
      }
    }

    _bindRow(el, on) {
      var fn = on ? 'addEventListener' : 'removeEventListener';
      el[fn]('pointerenter', this._onEnter);
      el[fn]('pointerleave', this._onLeave);
      el[fn]('pointerdown', this._onDown);
      el[fn]('pointermove', this._onMove);
      el[fn]('pointerup', this._onUp);
      el[fn]('pointercancel', this._onUp);
      el[fn]('click', this._onClick);
      el[fn]('focusin', this._onFocusIn);
      el[fn]('focusout', this._onFocusOut);
    }

    /* 悬停该行 → 暂停该行（另一行继续） */
    _onEnter(event) {
      var row = this._rowOf(event.currentTarget);
      if (row) row.hovered = true;
    }

    _onLeave(event) {
      var row = this._rowOf(event.currentTarget);
      if (row) row.hovered = false;
    }

    _onFocusIn(event) {
      var row = this._rowOf(event.currentTarget);
      if (row) row.focused = true;
    }

    _onFocusOut(event) {
      var row = this._rowOf(event.currentTarget);
      if (row) row.focused = false;
    }

    /* 左右拖拽：内容跟手，拖拽期间该行暂停 */
    _onDown(event) {
      var row = this._rowOf(event.currentTarget);
      if (!row || !row.period) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return; // 只响应左键
      row.dragging = true;
      this.drag = { row: row, startX: event.clientX, startPos: row.pos, id: event.pointerId };
      this._dragMoved = 0;
      this.classList.add('is-dragging');
      if (row.el.setPointerCapture && event.pointerId != null) {
        try {
          row.el.setPointerCapture(event.pointerId);
        } catch (err) {
          /* 部分环境不支持 pointer capture，降级为不捕获 */
        }
      }
      event.preventDefault();
    }

    _onMove(event) {
      if (!this.drag || this.drag.row.el !== event.currentTarget) return;
      var dx = event.clientX - this.drag.startX;
      if (Math.abs(dx) > Math.abs(this._dragMoved)) this._dragMoved = dx;
      // 向右手势 → 内容右移 → translate 变大 → pos 变小
      this.drag.row.pos = this.drag.startPos - dx;
      this._applyRow(this.drag.row);
      event.preventDefault();
    }

    _onUp(event) {
      if (!this.drag) return;
      var row = this.drag.row;
      row.dragging = false;
      if (row.el.releasePointerCapture && event && event.pointerId != null) {
        try {
          row.el.releasePointerCapture(event.pointerId);
        } catch (err) {
          /* 忽略 */
        }
      }
      this.drag = null;
      this.classList.remove('is-dragging');
      this._evaluate();
    }

    /* 拖拽后紧跟的 click 不当作点击（避免误跳转） */
    _onClick(event) {
      if (Math.abs(this._dragMoved) > DRAG_THRESHOLD) {
        this._dragMoved = 0;
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      this._dragMoved = 0;
    }

    _onResize() {
      var self = this;
      window.clearTimeout(this._resizeTimer);
      this._resizeTimer = window.setTimeout(function () {
        self._measure();
        self._evaluate();
      }, SETTLE_MS);
    }
  }

  if (!window.customElements.get('vl-why-velotric')) {
    window.customElements.define('vl-why-velotric', VLWhyVelotric);
  }
})();
